import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { RESET_PASSWORD } from "@/constants/testIds";
import { Activity, Eye, EyeOff, CheckCircle2, AlertCircle, ArrowLeft, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams]          = useSearchParams();
  const token                   = searchParams.get("token") || "";
  const navigate                = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState(false);

  useEffect(() => {
    if (!token) setError("Invalid or missing reset token. Please request a new password reset link.");
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50 items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/auth" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 rounded-lg bg-blue-900 flex items-center justify-center">
            <Activity size={18} className="text-white" />
          </div>
          <span className="text-slate-900 text-xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
            LabReport <span className="text-blue-900">AI</span>
          </span>
        </Link>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8" data-testid={RESET_PASSWORD.page}>

          {success ? (
            /* ── Success state ── */
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                Password updated!
              </h2>
              <p className="text-slate-500 text-sm mb-8 leading-relaxed">
                Your password has been reset successfully. You can now sign in with your new password.
              </p>
              <button
                data-testid={RESET_PASSWORD.goToLoginButton}
                onClick={() => navigate("/auth")}
                className="w-full bg-blue-900 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-blue-800 transition-colors">
                Sign in
              </button>
            </div>
          ) : (
            /* ── Form state ── */
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Lock size={18} className="text-blue-900" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Set new password
                  </h2>
                  <p className="text-slate-400 text-xs">Must be at least 6 characters</p>
                </div>
              </div>

              {error && (
                <div data-testid={RESET_PASSWORD.errorMessage}
                  className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-5">
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" /> {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">New password</label>
                  <div className="relative">
                    <input
                      data-testid={RESET_PASSWORD.passwordInput}
                      type={showPw ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 6 characters"
                      disabled={!token}
                      className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-10 disabled:bg-slate-50"
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm password</label>
                  <input
                    data-testid={RESET_PASSWORD.confirmInput}
                    type={showPw ? "text" : "password"}
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter your new password"
                    disabled={!token}
                    className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-slate-50"
                  />
                </div>

                <button
                  data-testid={RESET_PASSWORD.submitButton}
                  type="submit"
                  disabled={loading || !token}
                  className="w-full bg-blue-900 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-blue-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2">
                  {loading ? "Updating password..." : "Update password"}
                </button>
              </form>

              <div className="mt-5 text-center">
                <Link to="/auth"
                  className="text-slate-400 hover:text-slate-600 text-xs flex items-center gap-1 justify-center transition-colors">
                  <ArrowLeft size={12} /> Back to sign in
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
